import React from 'react';
import { action, observable } from 'mobx';
import { FormattedMessage } from 'react-intl';
import { SHORT_PLAIN_TEXT_LENGTH } from '../../../../constants/common.constants';

const validationFieldsMessages: { [key: string]: string } = {
    name: 'Name',
    organizationTicker: 'Ticker',
    organizationType: 'Type',
    externalOrganizationId: 'External Id',
};

export interface IHubOrganizationFormData {
    name: string;
    organizationTicker: string;
    organizationType: string;
    externalOrganizationId: string;
}

const defaultValues: IHubOrganizationFormData = {
    name: '',
    organizationTicker: '',
    organizationType: '',
    externalOrganizationId: '',
};

export class HubOrganizationFormDataViewModel {
    @observable
    public name: string;
    @observable
    public organizationTicker: string;
    @observable
    public organizationType: string;
    @observable
    public externalOrganizationId: string;
    @observable
    public formChecked: boolean;
    @observable
    private errors: { [key: string]: string } = {};
    @observable
    private serverErrorMessage: string = '';

    constructor(organizationDetails: IHubOrganizationFormData = defaultValues) {
        this.name = organizationDetails.name || defaultValues.name;
        this.organizationTicker = organizationDetails.organizationTicker || defaultValues.organizationTicker;
        this.organizationType = organizationDetails.organizationType || defaultValues.organizationType;
        this.externalOrganizationId = organizationDetails.externalOrganizationId || defaultValues.externalOrganizationId;
        this.formChecked = false;
    }

    @action
    public setName(value: string): void {
        this.name = value;
        this.clearError('name');
        this.setServerErrorMessage('');
    }

    @action
    public setType(value: string): void {
        this.organizationType = value;
        this.clearError('organizationType');
        this.setServerErrorMessage('');
    }

    @action
    public setTicker(value: string): void {
        this.organizationTicker = value;
        this.clearError('organizationTicker');
        this.setServerErrorMessage('');
    }

    @action
    public setExternalOrganizationId(value: string): void {
        this.externalOrganizationId = value;
        this.clearError('externalOrganizationId');
        this.setServerErrorMessage('');
    }

    @action
    private clearError(field: string): void {
        if (this.errors[field]) {
            delete this.errors[field];
        }
    }

    @action
    public setFieldError(field: string, message: string): void {
        this.errors[field] = message;
    }

    @action
    public clearAllFieldErrors(): void {
        this.errors = {};
    }

    @action
    public setServerErrorMessage(message: string): void {
        this.serverErrorMessage = message;
    }

    public getServerErrorMessage(): string {
        return this.serverErrorMessage;
    }

    @action
    public getNameError = (): JSX.Element | null => {
        return this.name
            ? this.getFieldError('name')
            : <FormattedMessage
                id='oe.errors.name.cantBeEmpty'
                description='Organization name label'
                defaultMessage="Name can't be empty"
            />;
    };

    public validateFieldsLength(): boolean {
        for (const field of Object.keys(validationFieldsMessages)) {
            if (this.getFieldError(field)) {
                return true;
            }
        }
        return false;
    }

    public getFieldError(field: string): JSX.Element | null {
        if (this.errors[field]) {
            return <>{this.errors[field]}</>;
        }
        const value = this[field];
        if (value?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return <>{`${validationFieldsMessages[field]} is too long`}</>;
        }
        return null;
    }

    public hasApiError(field: string): boolean {
        return !!this.errors[field];
    }

    @action
    public checkForm = (): boolean => {
        this.formChecked = true;
        return (
            !this.getNameError()
            && !this.validateFieldsLength()
        );
    };
}

